// src/helpers/iconManifest.ts

export type IconType = "icons" | "iconsdark";

export interface IIconEntry {
  key: string;
  path: string;
  date: number;
}

export interface IIconsRaw {
  screen_id: string;
  icons: Record<string, string>;
  icons_date?: Record<string, number>;
  iconsdark: Record<string, string>;
  iconsdark_date?: Record<string, number>;
}

export interface ILoadIcons {
  lastUpdate: number;
  data: IIconsRaw[];
}

//---------------------------------------------------------------- validação

const isObject = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === "object" && !Array.isArray(v);

/** Aceita apenas mapas chave→string; devolve {} para tudo o resto. */
function asPathMap(v: unknown): Record<string, string> {
  if (!isObject(v)) return {}; // getPage devolve "" p/ ecrã desconhecido
  const out: Record<string, string> = {};
  for (const [k, val] of Object.entries(v)) {
    if (typeof val === "string" && val) out[k] = val;
  }
  return out;
}

function asDateMap(v: unknown): Record<string, number> {
  if (!isObject(v)) return {};
  const out: Record<string, number> = {};
  for (const [k, val] of Object.entries(v)) {
    if (typeof val === "number" && Number.isFinite(val)) out[k] = val;
  }
  return out;
}

/** Valida o envelope. Devolve null se não for utilizável. */
export function parseLoadIcons(body: unknown): ILoadIcons | null {
  if (!isObject(body) || !Array.isArray(body.data)) return null;

  // O backend usa camelCase; tolerar snake_case caso seja normalizado.
  const lastUpdate = Number(body.lastUpdate ?? body.last_update ?? 0);
  if (!Number.isFinite(lastUpdate)) return null;

  const screens: IIconsRaw[] = [];
  for (const raw of body.data) {
    if (!isObject(raw)) continue;
    const screen_id = String(raw.screen_id ?? "");
    if (!screen_id) continue;

    screens.push({
      screen_id,
      icons: asPathMap(raw.icons),
      icons_date: asDateMap(raw.icons_date),
      iconsdark: asPathMap(raw.iconsdark),
      iconsdark_date: asDateMap(raw.iconsdark_date),
    });
  }

  if (screens.length === 0) return null; // catálogo vazio é suspeito
  return { lastUpdate, data: screens };
}

//---------------------------------------------------------------- leitura

/** Junta o mapa de caminhos com o de datas de um ecrã. */
export function iconEntries(
  page: IIconsRaw | undefined,
  type: IconType,
): IIconEntry[] {
  if (!page) return [];
  const paths = page[type] ?? {};
  const dates = type === "icons" ? page.icons_date : page.iconsdark_date;

  return Object.entries(paths).map(([key, path]) => ({
    key,
    path,
    date: dates?.[key] ?? 0,
  }));
}

/** Chave do índice: "1010|icons|close". */
export function refKey(screen: string, type: IconType, key: string): string {
  return `${screen}|${type}|${key}`;
}

/** Mapa caminho→data, desduplicado. Em conflito, fica a data mais recente. */
export function manifestPaths(screens: IIconsRaw[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const page of screens) {
    for (const type of ["icons", "iconsdark"] as const) {
      for (const e of iconEntries(page, type)) {
        if (out[e.path] === undefined || e.date > out[e.path])
          out[e.path] = e.date;
      }
    }
  }
  return out;
}

/** Resumo estável: muda se qualquer caminho ou data mudar. */
export function manifestFingerprint(screens: IIconsRaw[]): string {
  const parts: string[] = [];
  for (const page of screens) {
    for (const type of ["icons", "iconsdark"] as const) {
      for (const e of iconEntries(page, type)) {
        parts.push(`${page.screen_id}|${type}|${e.key}=${e.path}:${e.date}`);
      }
    }
  }
  parts.sort(); // a ordem das chaves JSON não é garantida

  let h = 5381;
  const joined = parts.join("\n");
  for (let i = 0; i < joined.length; i++) {
    h = ((h << 5) + h + joined.charCodeAt(i)) | 0;
  }
  return `${parts.length}.${(h >>> 0).toString(36)}`;
}
